/**
 * BlogNotificationSettings
 *
 * Sektion im Profil-Tab "Profil". Erlaubt jedem eingeloggten User, Blog-
 * Benachrichtigungs-Mails ein-/auszuschalten — auch nach Abo-Kündigung.
 * Pendant zum öffentlichen Unsubscribe-Link aus der Mail (siehe
 * /blog/unsubscribe), aber ohne Token-Magie.
 *
 * Backend: GET/PATCH /auth/user/blog-notifications
 */

import React from 'react';
import { Newspaper } from 'lucide-react';
import TWToggle from '../ui/TWToggle';
import { ProfileSection } from './ProfileSection';
import { NotificationToggleRow } from './NotificationToggleRow';
import { useBlogNotificationPreferences } from '../../hooks/useBlogNotificationPreferences';

export const BlogNotificationSettings: React.FC = () => {
  const { preferences, isLoading, error, setEnabled } = useBlogNotificationPreferences();

  const handleToggle = async (next: boolean): Promise<void> => {
    try {
      await setEnabled(next);
    } catch {
      /* Hook hat error gesetzt + zurückgerollt */
    }
  };

  return (
    <ProfileSection
      title="Blog-Benachrichtigungen"
      description="Erhalte eine E-Mail, sobald ein neuer Beitrag im Bitcoin-Blog erscheint."
      layout="stacked"
    >
      {isLoading && !preferences ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Lädt…</p>
      ) : (
        <div className="space-y-4">
          <NotificationToggleRow
            icon={<Newspaper className="w-4 h-4 text-gray-600 dark:text-gray-300" strokeWidth={2} />}
            title="E-Mail bei neuem Blog-Beitrag"
            description={
              <>
                Wir informieren dich per E-Mail über jeden neuen Beitrag. Auch nach Ablauf deines
                Abonnements bleibt diese Benachrichtigung aktiv, damit du nichts verpasst und dein
                Abo bei Interesse einfach reaktivieren kannst. Du kannst sie hier jederzeit ein-
                und ausschalten.
              </>
            }
            control={
              <TWToggle
                checked={preferences?.enabled ?? true}
                onChange={handleToggle}
                color="brand"
                label="Blog-Benachrichtigungen aktivieren"
              />
            }
          />

          {error && (
            <p className="text-xs text-red-500 mt-2">
              Fehler beim Speichern: {error.message}
            </p>
          )}
        </div>
      )}
    </ProfileSection>
  );
};

export default BlogNotificationSettings;
